import { useCallback, useEffect } from 'react';
import { Space, Title, Text } from '../../atoms';
import { Skeleton } from 'antd';
import { firebase, useCustomRouter, message } from '@vette/frontend-utils';
import {
  useAcceptUserInviteMutation,
  useClientByInvitationKeyQuery,
} from '@vette/data-access';
import { UserCredential } from 'firebase/auth';
import { FormValues } from '../AuthForm/types';
import { AuthForm } from '../AuthForm';

type Props = {
  redirectPath: string;
};

export const AcceptInvitation: React.FC<Props> = ({ redirectPath }) => {
  const { router } = useCustomRouter();
  const { user, logout, refetchToken } = firebase.useAuth();
  const query = new URLSearchParams(window.location.search);
  const invitationKey = query.get('key');

  const safeLogout = useCallback(async () => {
    if (user) await logout();
  }, [user, logout]);

  useEffect(() => {
    if (!invitationKey) {
      message.error('Sorry, this invitation link is no longer valid');
      router.replace('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading: clientUserInvitationLoading } =
    useClientByInvitationKeyQuery({
      variables: { invitationKey: invitationKey as string },
      skip: !invitationKey,
      onCompleted: ({ clientByInvitationKey }) => {
        if (clientByInvitationKey?.__typename === 'NotFoundError') {
          message.error('Sorry, this invitation link is no longer valid');
          router.replace('/404');
        }
      },
    });

  const [acceptUserInvite] = useAcceptUserInviteMutation({
    onCompleted: ({ acceptUserInvite }) => {
      if (acceptUserInvite?.__typename === 'User') {
        message.success('Invitation Accepted successfully');
      } else if (acceptUserInvite?.__typename === 'ValidationError') {
        message.error(
          acceptUserInvite.messages[0] || acceptUserInvite.messages
        );
      } else if (acceptUserInvite?.__typename === 'NotFoundError') {
        message.error(acceptUserInvite.message);
        router.replace('/404');
      }
    },
  });

  const onSuccess = async (
    user: UserCredential['user'],
    values: FormValues
  ) => {
    if (!invitationKey || !values.firstName || !values.lastName) {
      message.error('Some of required values are not provided.');
      return;
    }

    await acceptUserInvite({
      variables: {
        input: {
          firebaseUid: user.uid,
          invitationKey,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
        },
      },
    });

    await refetchToken();
    await firebase.auth.currentUser?.getIdTokenResult(true);
    window.location.href = redirectPath;
  };

  return (
    <AuthForm
      flow="acceptInvitation"
      onSuccess={onSuccess}
      safeLogout={safeLogout}
      header={
        <Space direction="vertical" size={4} $fullWidth>
          <Title $size={36}>
            Welcome
            <span role="img" aria-label="wave">
              👋
            </span>
          </Title>
          <span>
            <>
              <Text $size={15}>
                You’ve been invited to join the{' '}
                {clientUserInvitationLoading ? (
                  <Skeleton.Input active block={false} size="small" />
                ) : (
                  <>
                    {data?.clientByInvitationKey?.__typename === 'Client'
                      ? data.clientByInvitationKey.companyName
                      : ''}{' '}
                  </>
                )}
                team, please complete your personal information below.
              </Text>
            </>
          </span>
        </Space>
      }
    />
  );
};
